import React, { memo, useCallback, useMemo } from 'react';
import AsyncReactSelect, {
  Props as AsyncReactSelectProps,
} from 'react-select/async';
import ReactSelect, {
  OptionTypeBase,
  Props as ReactSelectProps,
} from 'react-select';
import { ActionMeta } from 'react-select';
import { useFormikContext } from 'formik';
import { useTheme } from 'react-jss';

import { Theme } from '@app/theme';
import { GenericInput } from '../GenericInput';
import { useReactSelectStyle } from './Select';
import { ChipColor } from '@app/core';
import { useTrackReactSelect } from './useReactSelectChangeLog';

interface Props {
  label?: string | React.ReactNode;
  containerClass?: string;
  name?: string;
  error?: string;
  noMargin?: boolean;
  noBottomMargin?: boolean;
  rightAction?: React.ReactNode;
  i18nKeyPrefix?: string;
  chipColor?: ChipColor;
  opaquePlaceholder?: boolean;
  useValue?: boolean;
}

type SelectProps<OptionType extends OptionTypeBase, IsMulti extends boolean> =
  | ReactSelectProps<OptionType, IsMulti>
  | AsyncReactSelectProps<OptionType, IsMulti>;

const InnerComponent = memo(
  <OptionType extends OptionTypeBase, IsMulti extends boolean>(
    props: SelectProps<OptionType, IsMulti>,
  ) => {
    const theme: Theme = useTheme();
    const chipColor = props.chipColor;
    const classes: any = useReactSelectStyle({
      theme,
      color: chipColor,
      opaquePlaceholder: props.opaquePlaceholder,
    });
    const onChange = useTrackReactSelect(props.trackingName, props.onChange);
    return (
      <label>
        {'loadOptions' in props ? (
          <AsyncReactSelect
            {...props}
            loadOptions={props.loadOptions}
            classNamePrefix="react-select"
            className={classes.picker}
            onChange={onChange}
          />
        ) : (
          <ReactSelect
            {...props}
            classNamePrefix="react-select"
            className={classes.picker}
            onChange={onChange}
          />
        )}
      </label>
    );
  },
);

const defaultGetOptionValue = (option: any) => option.value;
export const ObjectSelect = ({
  i18nKeyPrefix,
  onChange,
  name,
  children,
  useValue,
  value: propsValue,
  ...props
}: Props & ReactSelectProps<any, boolean>) => {
  const formikContext = useFormikContext<any>();
  const setFieldValue = formikContext && formikContext.setFieldValue;
  const getOptionValue = props.getOptionValue || defaultGetOptionValue;

  const _onChange = useCallback(
    (option: any, action: ActionMeta<any>) => {
      let res = props.isMulti ? option || [] : option;
      if (useValue) {
        res = props.isMulti
          ? option.map(getOptionValue)
          : getOptionValue(option);
      }

      if (onChange) {
        onChange(res, action);
      } else if (setFieldValue && name) {
        setFieldValue(name, res);
      }
    },
    [onChange, name, setFieldValue, props.isMulti, useValue, getOptionValue],
  );

  const value = useMemo(() => {
    const controlledValue =
      !formikContext || !name
        ? propsValue
        : formikContext.getFieldMeta(name).value;

    if (!useValue || !controlledValue || !props.options) {
      return controlledValue;
    }

    const find = (value: any) => {
      if (!props.options) {
        return value;
      }
      return props.options.find((option) => getOptionValue(option) === value);
    };

    if (props.isMulti) {
      return controlledValue.map(find);
    }
    return find(controlledValue);
  }, [
    formikContext,
    name,
    propsValue,
    props.isMulti,
    useValue,
    props.options,
    getOptionValue,
  ]);

  return (
    <React.Fragment>
      <GenericInput
        component={InnerComponent}
        {...props}
        onChange={_onChange}
        value={value}
        name={name}
      />
    </React.Fragment>
  );
};
